<template>
  <div
    class="og-chatbot-window"
    :class="[
      `og-chatbot-window--${theme}`,
      { 'og-chatbot-window--sidebar': layout === 'sidebar', 'og-chatbot-window--embedded': embedded },
    ]"
  >
    <div class="og-chatbot-window__header">
      <div class="og-chatbot-window__header-title-wrap">
        <img
          class="og-chatbot-window__header-avatar"
          :src="eloAvatarSrc"
          width="32"
          height="32"
          alt="Elô"
        />
        <h3 class="og-chatbot-window__title">{{ title }}</h3>
      </div>
      <div class="og-chatbot-window__header-actions">
        <button
          v-if="showThemeToggle"
          type="button"
          class="og-chatbot-window__theme-toggle"
        :aria-label="theme === 'light' ? 'Usar tema escuro' : 'Usar tema claro'"
        :title="theme === 'light' ? 'Tema escuro' : 'Tema claro'"
        @click="$emit('toggle-theme')"
      >
        <i
          v-if="theme === 'light'"
          class="moon icon"
          aria-hidden="true"
        />
        <i
          v-else
          class="sun icon"
          aria-hidden="true"
        />
      </button>
        <button
          v-if="showCloseButton"
          type="button"
          class="og-chatbot-window__close"
          aria-label="Fechar"
          title="Fechar"
          @click="$emit('close')"
        >
          <i class="times icon" aria-hidden="true" />
        </button>
      </div>
    </div>
    <div ref="messages" class="og-chatbot-window__messages">
      <div
        v-for="(msg, idx) in messages"
        :key="idx"
        class="og-chatbot-message"
        :class="`og-chatbot-message--${msg.role}`"
      >
        <div class="og-chatbot-message__bubble">
          <template v-if="msg.role === 'bot' && msg.html">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              class="og-chatbot-message__content"
              v-html="processHtml(msg.html)"
              @click="onBotHtmlClick"
            />
          </template>
          <template v-else>
            <div v-if="msg.attachments && msg.attachments.length" class="og-chatbot-message__user-attachments">
              <img
                v-for="(att, ai) in msg.attachments"
                :key="`img-${ai}`"
                v-show="att.type === 'image'"
                :src="att.url"
                :alt="'Imagem'"
                class="og-chatbot-message__user-img"
                role="button"
                tabindex="0"
                @click="openLightbox(att.url)"
                @keydown.enter.prevent="openLightbox(att.url)"
              />
              <audio
                v-for="(att, ai) in msg.attachments"
                v-show="att.type === 'audio'"
                :key="`audio-${ai}`"
                :src="att.url"
                preload="auto"
                controls
                class="og-chatbot-message__user-audio"
                v-audio-load-metadata
              />
            </div>
            <span v-if="msg.text">{{ msg.text }}</span>
          </template>
        </div>
      </div>
      <div v-if="loading" class="og-chatbot-message og-chatbot-message--bot">
        <div class="og-chatbot-message__bubble og-chatbot-message__typing">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>
    <div
      v-if="conversaEncerrada"
      class="og-chatbot-window__conversa-encerrada-wrap"
      role="region"
      :aria-label="conversaEncerradaTexto"
    >
      <div class="og-chatbot-window__conversa-encerrada-msg" role="status">
        <i class="check circle outline icon" aria-hidden="true" />
        <span>{{ conversaEncerradaTexto }}</span>
      </div>
      <a
        v-if="chamadoDeskLink"
        class="og-chatbot-window__chamado-desk-link"
        :href="chamadoDeskLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i class="external alternate icon" aria-hidden="true" />
        {{ chamadoDeskLinkTexto }}
      </a>
      <button
        v-if="showNovaConversaButton"
        type="button"
        class="og-chatbot-window__nova-conversa-cta"
        :disabled="loading"
        @click="$emit('nova-conversa')"
      >
        <i class="refresh icon" aria-hidden="true" />
        {{ novaConversaBotaoTexto }}
      </button>
    </div>
    <div v-if="attachedFiles.length > 0" class="og-chatbot-window__attachments">
      <div
        v-for="(f, i) in attachedFiles"
        :key="i"
        class="og-chatbot-attachment"
      >
        <template v-if="f.type.startsWith('image/')">
          <img
            :src="f.preview"
            :alt="f.name"
            class="og-chatbot-attachment__thumb"
            role="button"
            tabindex="0"
            @click="openLightbox(f.preview)"
            @keydown.enter.prevent="openLightbox(f.preview)"
          />
        </template>
        <template v-else>
          <i class="microphone icon og-chatbot-attachment__icon" />
          <span class="og-chatbot-attachment__name">{{ f.name }}</span>
        </template>
        <button
          type="button"
          class="og-chatbot-attachment__remove"
          aria-label="Remover"
          @click="removeFile(i)"
        >
          <i class="times icon" />
        </button>
      </div>
    </div>
    <form class="og-chatbot-window__form" @submit.prevent="send">
      <input
        ref="fileInput"
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
        multiple
        class="og-chatbot-window__file-input"
        @change="onFileSelect"
      />
      <button
        type="button"
        class="og-chatbot-window__attach"
        aria-label="Anexar imagem"
        title="Anexar imagem"
        :disabled="loading || isRecording"
        @click="$refs.fileInput.click()"
      >
        <i class="paperclip icon" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="og-chatbot-window__record"
        :class="{ 'og-chatbot-window__record--active': isRecording }"
        :aria-label="isRecording ? 'Parar gravação' : 'Gravar áudio'"
        :title="isRecording ? 'Parar gravação' : 'Gravar áudio'"
        :disabled="loading"
        @click="toggleRecording"
      >
        <i class="microphone icon" aria-hidden="true" />
      </button>
      <input
        v-model="inputText"
        type="text"
        class="og-chatbot-window__input"
        :placeholder="placeholder"
        :aria-busy="loading ? 'true' : 'false'"
        autocomplete="off"
      />
      <button
        type="submit"
        class="og-chatbot-window__submit"
        :disabled="(!inputText.trim() && attachedFiles.length === 0) || loading"
        aria-label="Enviar"
      >
        <i class="paper plane icon" aria-hidden="true" />
      </button>
    </form>
  </div>
</template>

<script>
import eloPng from '@/assets/elo.png';
import { mountLightboxInDocument } from './ogChatbotLightboxMount';

/**
 * Imagem ampliada no iframe: URLs http(s) → apenas postMessage para `window.parent` (a página pai
 * exibe o lightbox; contrato abaixo). URL `blob:` (preview de anexo) continua com lightbox no próprio
 * `document` do iframe (blob não costuma valer no pai).
 *
 * postMessage (iframe, url não-blob) — o pai implementa o listener:
 *   { source: 'eloca-chatbot', action: 'og-chatbot-image-lightbox', url: string }
 * Fechar overlay no filho / nova conversa:
 *   { source: 'eloca-chatbot', action: 'og-chatbot-image-lightbox-request-close' }
 * Opcional — pai avisa o iframe que fechou (sync de estado):
 *   { source: 'eloca-chatbot', action: 'og-chatbot-image-lightbox-closed' }
 */
export default {
  name: 'ChatbotWindow',
  directives: {
    audioLoadMetadata: {
      inserted(el) {
        if (el.tagName === 'AUDIO') el.load();
      },
    },
  },
  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Chat',
    },
    placeholder: {
      type: String,
      default: 'Digite sua mensagem...',
    },
    theme: {
      type: String,
      default: 'light',
      validator: (v) => ['light', 'dark'].includes(v),
    },
    showThemeToggle: {
      type: Boolean,
      default: false,
    },
    showCloseButton: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: 'popup',
      validator: (v) => ['popup', 'sidebar'].includes(v || 'popup'),
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    conversaEncerrada: {
      type: Boolean,
      default: false,
    },
    conversaEncerradaTexto: {
      type: String,
      default: 'Esta conversa foi encerrada.',
    },
    /** URL do chamado no Desk (nova aba); vem de `chamado_desk_link` na API/Pusher */
    chamadoDeskLink: {
      type: String,
      default: '',
    },
    chamadoDeskLinkTexto: {
      type: String,
      default: 'Abrir chamado no Desk',
    },
    showNovaConversaButton: {
      type: Boolean,
      default: true,
    },
    /** Rótulo do botão “Nova conversa” (banner encerrada + linha discreta) */
    novaConversaBotaoTexto: {
      type: String,
      default: 'Iniciar nova conversa',
    },
    /** Incrementado pelo pai para limpar input/anexos ao iniciar nova conversa */
    resetKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      eloAvatarSrc: eloPng,
      inputText: '',
      attachedFiles: [],
      isRecording: false,
      mediaRecorder: null,
      recordStream: null,
      /** { destroy } retornado por `mountLightboxInDocument` (DOM fora do painel com `contain:`) */
      lightboxHandle: null,
      /** 'none' | 'dom' | 'postmessage' (overlay na página pai via postMessage) */
      lightboxMode: 'none',
    };
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.onMessageFromAppLightbox);
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.onMessageFromAppLightbox);
    }
    this.stopRecordingInternal();
    this.closeLightbox();
  },
  watch: {
    messages() {
      this.$nextTick(() => this.scrollToBottom());
    },
    loading() {
      this.$nextTick(() => this.scrollToBottom());
    },
    resetKey() {
      this.closeLightbox();
      this.inputText = '';
      this.attachedFiles.forEach((f) => {
        if (f && f.preview) URL.revokeObjectURL(f.preview);
      });
      this.attachedFiles = [];
      this.stopRecordingInternal();
    },
  },
  methods: {
    clearLightboxState() {
      this.lightboxMode = 'none';
      this.lightboxHandle = null;
    },
    tryMountInWindow(targetWin, url) {
      if (!targetWin || !url) return null;
      let doc;
      try {
        doc = targetWin.document;
        if (!doc || !doc.body) return null;
      } catch (e) {
        return null;
      }
      return mountLightboxInDocument(doc, targetWin, url, {
        onClose: () => {
          this.clearLightboxState();
        },
      });
    },
    onMessageFromAppLightbox(e) {
      const d = e.data;
      if (!d || d.source !== 'eloca-chatbot' || d.action !== 'og-chatbot-image-lightbox-closed') {
        return;
      }
      if (this.lightboxMode === 'postmessage') {
        this.clearLightboxState();
      }
    },
    openLightboxPostMessage(url) {
      this.lightboxMode = 'postmessage';
      if (typeof window === 'undefined' || !window.parent) return;
      console.log('openLightboxPostMessage', url);
      window.parent.postMessage(
        { source: 'eloca-chatbot', action: 'og-chatbot-image-lightbox', url },
        '*',
      );
    },
    openLightbox(url) {
      if (!url || typeof url !== 'string') return;
      this.closeLightbox();
      const inIframe = typeof window !== 'undefined' && window.parent && window.parent !== window;
      if (inIframe && url.indexOf('blob:') === 0) {
        const m = this.tryMountInWindow(window, url);
        if (m) {
          this.lightboxHandle = m;
          this.lightboxMode = 'dom';
        }
        return;
      }
      if (inIframe) {
        this.openLightboxPostMessage(url);
        return;
      }
      const m = this.tryMountInWindow(window, url);
      if (m) {
        this.lightboxHandle = m;
        this.lightboxMode = 'dom';
      }
    },
    closeLightbox() {
      if (this.lightboxMode === 'postmessage') {
        this.clearLightboxState();
        try {
          if (window.parent) {
            window.parent.postMessage(
              { source: 'eloca-chatbot', action: 'og-chatbot-image-lightbox-request-close' },
              '*',
            );
          }
        } catch (e) {
          /* ignorado */
        }
        return;
      }
      if (this.lightboxHandle && typeof this.lightboxHandle.destroy === 'function') {
        this.lightboxHandle.destroy();
        return;
      }
      this.clearLightboxState();
    },
    onBotHtmlClick(e) {
      const root = e.currentTarget;
      const img = e.target.closest && e.target.closest('img');
      if (img && root.contains(img)) {
        e.preventDefault();
        e.stopPropagation();
        const src = img.currentSrc || img.src;
        if (src) this.openLightbox(src);
        return;
      }
      const a = e.target.closest && e.target.closest('a');
      if (!a || !root.contains(a)) return;
      const hrefAttr = a.getAttribute('href');
      if (!hrefAttr) return;
      const scheme = hrefAttr.trim().split(':')[0].toLowerCase();
      if (scheme === 'javascript') return;
      e.preventDefault();
      e.stopPropagation();
      const url = a.href;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    processHtml(html) {
      if (!html || typeof html !== 'string') return html;
      let out = html.replace(
        /<img\s+([^>]*?)src=["']([^"']+)["']([^>]*)>/gi,
        (match, before, src, after) => {
          const encodedSrc = encodeURIComponent(src);
          const proxyUrl = `https://wsrv.nl/?url=${encodedSrc}&n=-1`;
          return `<img ${before}src="${proxyUrl}" referrerpolicy="no-referrer"${after}>`;
        },
      );
      out = out.replace(/<a(\s[^>]*?)>/gi, (full, attrs) => {
        if (/\starget\s*=/i.test(attrs)) return full;
        return `<a target="_blank" rel="noopener noreferrer"${attrs}>`;
      });
      return out;
    },
    send() {
      const text = this.inputText.trim();
      const files = [...this.attachedFiles];
      if ((!text && files.length === 0) || this.loading) return;
      this.$emit('send', text || '(arquivo anexado)', files.map(f => f.file));
      this.inputText = '';
      this.attachedFiles = [];
    },
    onFileSelect(e) {
      const inputs = e.target.files;
      if (!inputs || inputs.length === 0) return;
      for (let i = 0; i < inputs.length; i += 1) {
        const file = inputs[i];
        const type = file.type.startsWith('image/') ? 'image' : 'audio';
        const preview = type === 'image' ? URL.createObjectURL(file) : null;
        this.attachedFiles.push({
          file,
          name: file.name,
          type: file.type,
          preview,
        });
      }
      e.target.value = '';
    },
    removeFile(index) {
      const f = this.attachedFiles[index];
      if (f && f.preview) URL.revokeObjectURL(f.preview);
      this.attachedFiles.splice(index, 1);
    },
    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecordingInternal();
      } else {
        await this.startRecording();
      }
    },
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.recordStream = stream;
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
        const recorder = new MediaRecorder(stream);
        const chunks = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mimeType });
          const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
          const file = new File([blob], `gravacao-${Date.now()}.${ext}`, { type: blob.type });
          this.attachedFiles.push({
            file,
            name: file.name,
            type: file.type,
            preview: null,
          });
        };
        recorder.start(100);
        this.mediaRecorder = recorder;
        this.isRecording = true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Erro ao acessar microfone:', err);
      }
    },
    stopRecordingInternal() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      if (this.recordStream) {
        this.recordStream.getTracks().forEach((t) => t.stop());
        this.recordStream = null;
      }
      this.mediaRecorder = null;
      this.isRecording = false;
    },
    scrollToBottom() {
      const el = this.$refs.messages;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style lang="scss" scoped src="./ChatbotWindow.scss"></style>
